import React, { useEffect, useState, useMemo, useRef } from "react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';

// Decode JWT utility function
// Enhanced Decode JWT utility function
const decodeToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    console.log("Decoded token payload:", payload); // Debug log
    return payload;
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

// Date utility functions
const getWeekRange = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const weekStart = new Date(d.setDate(diff));
  const weekEnd = new Date(d.setDate(diff + 6));
  return { start: weekStart, end: weekEnd };
};

const formatDateForInput = (date) => {
  return date.toISOString().split('T')[0];
};

const getMonthName = (month) => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                 'July', 'August', 'September', 'October', 'November', 'December'];
  return months[month];
};

const formatDateForPrint = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};


const usePerformanceData = () => {
  const [data, setData] = useState({ 
    employees: [], 
    reviews: {}, 
    tickets: [] 
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchPerformance = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("No access token found");
        
        const decoded = decodeToken(token);
        
        // FIX: Get company ID from the correct field based on user type
        let companyId;
        if (decoded?.companyId) {
          // For employees: companyId is in the companyId field
          companyId = decoded.companyId;
        } else if (decoded?.id && decoded?.role === 'company') {
          // For company users: id is the company ID
          companyId = decoded.id;
        } else if (decoded?.id) {
          // Fallback: use id as company ID
          companyId = decoded.id;
        }
        
        if (!companyId) throw new Error("Invalid token: Missing company ID");

        console.log("Company ID from token:", companyId); // Debug log
        console.log("Decoded token:", decoded); // Debug log

        // Fetch employees, tickets, and departments in parallel
        const [empRes, ticketRes, deptRes] = await Promise.all([
          fetch("https://customersupport-jhy9.onrender.com/api/employees", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch("https://customersupport-jhy9.onrender.com/api/support-ticket", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`https://customersupport-jhy9.onrender.com/api/departments/company/${companyId}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        ]);

        if (!empRes.ok) throw new Error("Failed to fetch employees");
        if (!ticketRes.ok) throw new Error("Failed to fetch tickets");
        if (!deptRes.ok) throw new Error("Failed to fetch departments");

        const empData = await empRes.json();
        const ticketData = await ticketRes.json();
        const deptData = await deptRes.json();
        
        // Create department map
        const deptMap = {};
        deptData.forEach(dept => {
          deptMap[dept._id] = dept.name;
        });

        // Filter employees by company and enhance with department names
        const companyEmployees = Array.isArray(empData) 
          ? empData.filter(emp => emp.company === companyId)
              .map(emp => ({
                ...emp,
                departmentName: deptMap[emp.department] || emp.department || 'Unknown'
              }))
          : [];
        
        const allTickets = Array.isArray(ticketData) ? ticketData : 
                       ticketData.tickets || [];

        console.log("Total tickets fetched:", allTickets.length); // Debug log
        console.log("Company employees:", companyEmployees.length); // Debug log
        
        // CRITICAL FIX: Filter tickets by company
        // Get employee IDs for this company to filter tickets
        const companyEmployeeIds = companyEmployees.map(emp => emp._id);
        
        // Filter tickets: only include tickets assigned to employees of this company
        const companyTickets = allTickets.filter(ticket => {
          // Include tickets that are assigned to employees of this company
          return ticket.assignedTo && companyEmployeeIds.includes(ticket.assignedTo);
        });

        console.log("Company tickets after filtering:", companyTickets.length); // Debug log

        // Enhanced ticket processing with signed status
      // FIX: Use the same enhancement logic as Dashboard
const enhancedTickets = allTickets
  .map((ticket) => {
    const employee = empData.find(emp => emp._id === ticket.customerId);

    const assignedEmployeeName = ticket.assignedTo
      ? (empData.find(emp => emp._id === ticket.assignedTo)?.teamMemberName || "Unknown Employee")
      : null;

    const departmentId = employee ? employee.department : null;
    const departmentName = departmentId ? (deptData.find(dept => dept._id === departmentId)?.name || "Unknown") : "Company";

    return {
      ...ticket,
      employeeName: employee ? employee.teamMemberName : "Company",
      departmentId,
      department: departmentName,
      company: ticket.company || companyId,
      assignedEmployee: ticket.assignedEmployee || "",
      assignedTo: ticket.assignedTo || null,
      assignedEmployeeName,
      assignedDate: ticket.assignedDate || null,
      assignedBy: ticket.assignedBy || null,
      reassignDescription: ticket.reassignDescription || "",
      reassignedTo: ticket.reassignedTo || null,
      reassignedBy: ticket.reassignedBy || null,
      reassignedDate: ticket.reassignedDate || null,
      updatedAt: ticket.updatedAt || null,
      createdAt: ticket.createdAt || null,
      fieldWorkAssigned: ticket.fieldWorkAssigned || false,
      isAssigned: !!ticket.assignedTo,
      material: ticket.material || "",
      service: ticket.service || "",
      location: ticket.location || "",
      reopenReason: ticket.reopenReason || "",
      // Handle signed status
      isSigned: ticket.isSigned || false,
      signedAt: ticket.signedAt || null,
    };
  })
  .filter((t) =>
    t.company === companyId && (empData.some(emp => emp._id === t.customerId) || t.customerId === companyId)
  )
  .sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime() || 0;
    const dateB = new Date(b.createdAt).getTime() || 0;
    return dateB - dateA; // Newest first
  });

console.log("Enhanced tickets after filtering:", enhancedTickets.length);

        // Fetch reviews only for company tickets
        const reviewsByEmployee = {};
        const reviewPromises = companyTickets
          .filter(ticket => ticket.assignedTo)
          .map(async (ticket) => {
            try {
              const reviewRes = await fetch(
                `https://customersupport-jhy9.onrender.com/api/ticket-review/${ticket._id}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              if (reviewRes.ok) {
                const review = await reviewRes.json();
                // Only include reviews that have a rating
                if (review.rating) {
                  const employeeId = ticket.assignedTo;
                  
                  if (!reviewsByEmployee[employeeId]) {
                    reviewsByEmployee[employeeId] = [];
                  }
                  
                  reviewsByEmployee[employeeId].push({
                    ...review,
                    ticketId: ticket._id,
                    ticketSubject: ticket.subject,
                    isSigned: ticket.isSigned || false,
                    signedAt: ticket.signedAt,
                    createdAt: review.createdAt || ticket.updatedAt || new Date().toISOString(),
                  });
                }
              }
            } catch (err) {
              console.warn(`Failed to fetch review for ticket ${ticket._id}:`, err);
            }
          });
        
        await Promise.allSettled(reviewPromises);
        
        setData({ 
          employees: companyEmployees, 
          reviews: reviewsByEmployee,
          tickets: enhancedTickets
        });
      } catch (err) {
        console.error("Performance data fetch error:", err);
        setError(err.message || "Error fetching performance data");
      } finally {
        setLoading(false);
      }
    };

    fetchPerformance();
  }, []);

  return { data, loading, error };
};

const PerformanceFilters = ({ 
  filters, 
  onFilterChange, 
  employees,
  uniqueStatuses 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Report Filters</h3>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          {isExpanded ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${isExpanded ? 'block' : 'hidden'}`}>
        {/* Employee Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Employee
          </label>
          <select
            value={filters.employeeId || ''}
            onChange={(e) => onFilterChange('employeeId', e.target.value)}
            className="w-full border border-gray-300 rounded-md p-2 text-sm"
          >
            <option value="">All Employees</option>
            {employees.map(emp => (
              <option key={emp._id} value={emp._id}>
                {emp.teamMemberName}
              </option>
            ))}
          </select>
        </div>

        {/* Signed Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Signed Status
          </label>
          <select
            value={filters.signedStatus || ''}
            onChange={(e) => onFilterChange('signedStatus', e.target.value)}
            className="w-full border border-gray-300 rounded-md p-2 text-sm"
          >
            <option value="">All</option>
            <option value="signed">Signed</option>
            <option value="not-signed">Not Signed</option>
          </select>
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ticket Status
          </label>
          <select
            value={filters.status || ''}
            onChange={(e) => onFilterChange('status', e.target.value)}
            className="w-full border border-gray-300 rounded-md p-2 text-sm"
          >
            <option value="">All Statuses</option>
            {uniqueStatuses.map(status => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        {/* Date Range Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date Range
          </label>
          <select
            value={filters.dateRange || ''}
            onChange={(e) => onFilterChange('dateRange', e.target.value)}
            className="w-full border border-gray-300 rounded-md p-2 text-sm"
          >
            <option value="">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>
      </div>

      {/* Custom Date Range */}
      {filters.dateRange === 'custom' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={filters.startDate || ''}
              onChange={(e) => onFilterChange('startDate', e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => onFilterChange('endDate', e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
            />
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex justify-between items-center mt-4">
        <button
          onClick={() => onFilterChange('reset', null)}
          className="text-sm text-gray-600 hover:text-gray-800"
        >
          Clear All Filters
        </button>
        <div className="text-sm text-gray-500">
          Use filters to generate specific reports
        </div>
      </div>
    </div>
  );
};

// Print Component with Graphs
const PrintButton = ({ filteredData, filters, employeesWithData }) => {
  const printContentRef = useRef();

  const handlePrint = async () => {
    const printWindow = window.open('', '_blank');
    const printDocument = printWindow.document;

    // Get chart data for printing
    const chartData = getChartData(filteredData);
    const employeeStats = await getEmployeeStatsWithCharts(filteredData); // Enhanced to include charts

    // Generate global chart images
    const chartImages = await generateChartImages(chartData);

    printDocument.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Employee Performance Report</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              color: #333;
            }
            .print-header { 
              text-align: center; 
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .print-header h1 { 
              margin: 0; 
              color: #1f2937;
              font-size: 28px;
            }
            .print-header .subtitle { 
              color: #6b7280; 
              margin-top: 5px;
              font-size: 16px;
            }
            .summary-grid { 
              display: grid; 
              grid-template-columns: repeat(4, 1fr); 
              gap: 15px; 
              margin: 20px 0; 
            }
            .summary-card { 
              padding: 15px; 
              border-radius: 8px; 
              text-align: center;
              border: 1px solid #e5e7eb;
            }
            .summary-number { 
              font-size: 24px; 
              font-weight: bold; 
              margin-bottom: 5px;
            }
            .chart-container { 
              margin: 30px 0; 
              page-break-inside: avoid;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 20px;
              background: white;
            }
            .chart-title { 
              font-size: 18px; 
              font-weight: bold; 
              margin-bottom: 15px;
              border-bottom: 1px solid #e5e7eb;
              padding-bottom: 5px;
              color: #1f2937;
            }
            .chart-image {
              width: 100%;
              max-width: 600px;
              height: auto;
              margin: 0 auto;
              display: block;
            }
            .chart-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin: 20px 0;
            }
            .employee-card { 
              border: 1px solid #e5e7eb; 
              padding: 15px; 
              margin: 15px 0; 
              border-radius: 8px;
              page-break-inside: avoid;
              background: #fafafa;
            }
            .employee-header { 
              display: flex; 
              justify-content: space-between; 
              margin-bottom: 10px;
              align-items: center;
            }
            .employee-chart-container {
              margin: 15px 0;
              text-align: center;
              border: 1px solid #e5e7eb;
              border-radius: 6px;
              padding: 10px;
              background: white;
            }
            .employee-chart-title {
              font-size: 14px;
              font-weight: bold;
              margin-bottom: 10px;
              color: #1f2937;
            }
            .stats-grid { 
              display: grid; 
              grid-template-columns: repeat(2, 1fr); 
              gap: 10px; 
              margin: 10px 0;
            }
            .status-grid { 
              display: grid; 
              grid-template-columns: repeat(3, 1fr); 
              gap: 8px; 
              margin: 10px 0;
            }
            .status-item { 
              padding: 8px; 
              border-radius: 4px; 
              text-align: center;
              font-size: 12px;
              background: #f3f4f6;
              border: 1px solid #e5e7eb;
            }
            .filters-info { 
              background: #f3f4f6; 
              padding: 15px; 
              border-radius: 6px; 
              margin: 15px 0;
              font-size: 14px;
              border-left: 4px solid #3b82f6;
            }
            .legend {
              display: flex;
              flex-wrap: wrap;
              gap: 10px;
              margin-top: 15px;
              justify-content: center;
            }
            .legend-item {
              display: flex;
              align-items: center;
              gap: 5px;
              font-size: 12px;
            }
            .legend-color {
              width: 12px;
              height: 12px;
              border-radius: 2px;
            }
            .no-data {
              text-align: center;
              color: #6b7280;
              font-style: italic;
              padding: 20px;
            }
            @media print {
              body { margin: 0.5in; }
              .summary-grid { grid-template-columns: repeat(4, 1fr); }
              .no-print { display: none; }
              .chart-container { break-inside: avoid; }
              .employee-card { break-inside: avoid; }
              .employee-chart-container { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>Employee Performance Report</h1>
            <div class="subtitle">Generated on ${new Date().toLocaleDateString()}</div>
            ${getFiltersText(filters)}
          </div>

          ${generateSummaryHTML(filteredData)}
          ${generateChartsHTML(chartData, chartImages)}
          ${generateEmployeeDetailsHTML(employeeStats)}
          
          <div style="text-align: center; margin-top: 40px; color: #6b7280; font-size: 12px; border-top: 1px solid #e5e7eb; padding-top: 20px;">
            Report generated by CRM System • ${new Date().toLocaleString()}
          </div>
        </body>
      </html>
    `);

    printDocument.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Enhanced: Generate employee stats with individual trend charts
  const getEmployeeStatsWithCharts = async (filteredData) => {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = 400; // Smaller for per-employee charts
    tempCanvas.height = 250;

    const statsWithCharts = await Promise.all(
      filteredData.employees.map(async (emp) => {
        const empTickets = filteredData.tickets.filter(t => t.assignedTo === emp._id);
        const empReviews = filteredData.reviews[emp._id] || [];
        
        const statusCounts = empTickets.reduce((acc, ticket) => {
          const status = ticket.status || 'unknown';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {});

        const signedTickets = empTickets.filter(t => t.isSigned).length;
        const completionRate = empTickets.length > 0 
          ? Math.round((empTickets.filter(t => 
              ['completed', 'resolved', 'closed'].includes(t.status)
            ).length / empTickets.length) * 100)
          : 0;

        const avgRating = empReviews.length > 0
          ? (empReviews.reduce((sum, r) => sum + r.rating, 0) / empReviews.length).toFixed(2)
          : 'N/A';

        // Generate daily completed tickets chart
        const trendChartImage = await generateEmployeeTrendChart(empTickets, tempCanvas);

        return {
          ...emp,
          totalTickets: empTickets.length,
          signedTickets,
          completionRate,
          avgRating,
          statusCounts,
          trendChartImage  // Base64 image for print
        };
      })
    );

    return statsWithCharts.filter(emp => emp.totalTickets > 0); // Only include employees with data
  };

  // NEW: Generate Line Chart for Daily Completed Tickets (Monthly View - Last 30 Days)
  const generateEmployeeTrendChart = (empTickets, canvas) => {
    return new Promise((resolve) => {
      const ctx = canvas.getContext('2d');
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Filter completed tickets and group by day (last 30 days)
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const completedTickets = empTickets.filter(t => 
        ['completed', 'resolved', 'closed'].includes(t.status) &&
        new Date(t.updatedAt) >= thirtyDaysAgo
      );

      // Group by date (YYYY-MM-DD)
      const dailyData = completedTickets.reduce((acc, ticket) => {
        const date = new Date(ticket.updatedAt).toISOString().split('T')[0];
        acc[date] = (acc[date] || 0) + 1;
        return acc;
      }, {});

      // Sort dates and prepare chart data
      const dates = Object.keys(dailyData).sort();
      const chartData = dates.map(date => ({
        date,
        value: dailyData[date]
      }));

      if (chartData.length === 0) {
        // No data: Draw message
        ctx.fillStyle = '#6b7280';
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('No completed tickets', canvas.width / 2, canvas.height / 2 - 10);
        ctx.font = '12px Arial';
        ctx.fillText('in the last 30 days', canvas.width / 2, canvas.height / 2 + 10);
        resolve(canvas.toDataURL('image/png'));
        return;
      }

      // Chart configuration
      const margin = { top: 30, right: 20, bottom: 40, left: 40 };
      const chartWidth = canvas.width - margin.left - margin.right;
      const chartHeight = canvas.height - margin.top - margin.bottom;
      
      const maxValue = Math.max(...chartData.map(item => item.value));
      const xScale = chartWidth / (chartData.length - 1 || 1);
      
      // Draw axes
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(margin.left, margin.top);
      ctx.lineTo(margin.left, margin.top + chartHeight);
      ctx.lineTo(margin.left + chartWidth, margin.top + chartHeight);
      ctx.stroke();

      // Draw grid lines (Y-axis)
      for (let i = 0; i <= 5; i++) {
        const y = margin.top + (chartHeight / 5) * i;
        ctx.beginPath();
        ctx.moveTo(margin.left, y);
        ctx.lineTo(margin.left + chartWidth, y);
        ctx.strokeStyle = '#f3f4f6';
        ctx.stroke();
      }

      // Draw line
      ctx.beginPath();
      ctx.strokeStyle = '#10b981'; // Green for completed
      ctx.lineWidth = 2;
      
      chartData.forEach((item, index) => {
        const x = margin.left + index * xScale;
        const y = margin.top + chartHeight - (item.value / maxValue) * chartHeight;
        
        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      
      ctx.stroke();
      
      // Draw points
      chartData.forEach((item, index) => {
        const x = margin.left + index * xScale;
        const y = margin.top + chartHeight - (item.value / maxValue) * chartHeight;
        
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, 2 * Math.PI);
        ctx.fillStyle = '#10b981';
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();
      });
      
      // X-axis labels (dates, every 7 days for readability)
      ctx.fillStyle = '#333';
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      chartData.filter((_, index) => index % 7 === 0).forEach((item, index) => {
        const x = margin.left + (index * 7) * xScale;
        ctx.fillText(item.date.split('-').slice(1).join('/'), x, margin.top + chartHeight + 5);
      });
      
      // Y-axis labels
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      for (let i = 0; i <= 5; i++) {
        const value = Math.round((maxValue / 5) * i);
        const y = margin.top + chartHeight - (i / 5) * chartHeight;
        ctx.fillText(value, margin.left - 5, y);
      }
      
      // Title
      ctx.textAlign = 'center';
      ctx.font = 'bold 12px Arial';
      ctx.fillText('Daily Completed Tickets (Last 30 Days)', canvas.width / 2, margin.top - 10);
      
      resolve(canvas.toDataURL('image/png'));
    });
  };

  // Function to generate global chart images using Chart.js (unchanged, but kept for completeness)
  const generateChartImages = async (chartData) => {
    const images = {};
    
    // Create a temporary canvas for each chart
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = 600;
    tempCanvas.height = 400;
    const ctx = tempCanvas.getContext('2d');

    try {
      // Generate Pie Chart for status distribution
      if (chartData.statusData.length > 0) {
        images.statusPie = await generatePieChart(chartData.statusData, tempCanvas);
      }

      // Generate Bar Chart for employee performance
      if (chartData.employeeData.length > 0) {
        images.employeeBar = await generateBarChart(chartData.employeeData, tempCanvas);
      }

      // Generate Line Chart for trends
      if (chartData.trendData) {
        images.trendLine = await generateLineChart(chartData.trendData, tempCanvas);
      }

    } catch (error) {
      console.error('Error generating chart images:', error);
    }

    return images;
  };

  // ... (Keep all other helper functions unchanged: generatePieChart, generateBarChart, generateLineChart, getFiltersText, getChartData, generateTrendData, getSummaryStats, generateSummaryHTML, generateChartsHTML)




// Generate Pie Chart
  const generatePieChart = (data, canvas) => {
    return new Promise((resolve) => {
      const ctx = canvas.getContext('2d');
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Chart configuration
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const radius = Math.min(centerX, centerY) - 20;
      
      const colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];
      
      let total = data.reduce((sum, item) => sum + item.value, 0);
      let startAngle = 0;
      
      // Draw pie slices
      data.forEach((item, index) => {
        const sliceAngle = (item.value / total) * 2 * Math.PI;
        const endAngle = startAngle + sliceAngle;
        
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, startAngle, endAngle);
        ctx.closePath();
        
        ctx.fillStyle = colors[index % colors.length];
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        startAngle = endAngle;
      });
      
      // Add labels
      ctx.fillStyle = '#333';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      
      startAngle = 0;
      data.forEach((item, index) => {
        const sliceAngle = (item.value / total) * 2 * Math.PI;
        const labelAngle = startAngle + sliceAngle / 2;
        const labelRadius = radius * 0.7;
        
        const x = centerX + Math.cos(labelAngle) * labelRadius;
        const y = centerY + Math.sin(labelAngle) * labelRadius;
        
        const percentage = ((item.value / total) * 100).toFixed(1);
        ctx.fillText(`${percentage}%`, x, y);
        
        startAngle += sliceAngle;
      });
      
      resolve(canvas.toDataURL('image/png'));
    });
  };

  // Generate Bar Chart
  const generateBarChart = (data, canvas) => {
    return new Promise((resolve) => {
      const ctx = canvas.getContext('2d');
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Chart configuration
      const margin = { top: 40, right: 40, bottom: 60, left: 60 };
      const chartWidth = canvas.width - margin.left - margin.right;
      const chartHeight = canvas.height - margin.top - margin.bottom;
      
      const maxValue = Math.max(...data.map(item => Math.max(item.total, item.completed)));
      const barWidth = chartWidth / (data.length * 3);
      const xScale = chartWidth / data.length;
      
      // Draw bars
      data.forEach((item, index) => {
        const x = margin.left + index * xScale + barWidth;
        
        // Total tickets bar
        const totalHeight = (item.total / maxValue) * chartHeight;
        ctx.fillStyle = '#3b82f6';
        ctx.fillRect(x, margin.top + chartHeight - totalHeight, barWidth, totalHeight);
        
        // Completed tickets bar
        const completedHeight = (item.completed / maxValue) * chartHeight;
        ctx.fillStyle = '#10b981';
        ctx.fillRect(x + barWidth, margin.top + chartHeight - completedHeight, barWidth, completedHeight);
        
        // Employee name
        ctx.fillStyle = '#333';
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(
          item.name.length > 10 ? item.name.substring(0, 10) + '...' : item.name,
          x + barWidth,
          margin.top + chartHeight + 20
        );
      });
      
      // Y-axis labels
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      for (let i = 0; i <= 5; i++) {
        const value = Math.round((maxValue / 5) * i);
        const y = margin.top + chartHeight - (i / 5) * chartHeight;
        ctx.fillText(value, margin.left - 10, y);
      }
      
      // Chart title
      ctx.textAlign = 'center';
      ctx.font = 'bold 16px Arial';
      ctx.fillText('Employee Performance Overview', canvas.width / 2, margin.top - 10);
      
      // Legend
      ctx.font = '12px Arial';
      ctx.textAlign = 'left';
      ctx.fillStyle = '#3b82f6';
      ctx.fillRect(margin.left, margin.top + chartHeight + 40, 15, 15);
      ctx.fillStyle = '#333';
      ctx.fillText('Total Tickets', margin.left + 25, margin.top + chartHeight + 50);
      
      ctx.fillStyle = '#10b981';
      ctx.fillRect(margin.left + 120, margin.top + chartHeight + 40, 15, 15);
      ctx.fillStyle = '#333';
      ctx.fillText('Completed Tickets', margin.left + 145, margin.top + chartHeight + 50);
      
      resolve(canvas.toDataURL('image/png'));
    });
  };

  // Generate Line Chart (for trends)
  const generateLineChart = (data, canvas) => {
    return new Promise((resolve) => {
      const ctx = canvas.getContext('2d');
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // If no trend data, return empty
      if (!data || data.length === 0) {
        resolve(canvas.toDataURL('image/png'));
        return;
      }
      
      // Chart configuration
      const margin = { top: 40, right: 40, bottom: 60, left: 60 };
      const chartWidth = canvas.width - margin.left - margin.right;
      const chartHeight = canvas.height - margin.top - margin.bottom;
      
      const maxValue = Math.max(...data.map(item => item.value));
      const xScale = chartWidth / (data.length - 1);
      
      // Draw line
      ctx.beginPath();
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 3;
      
      data.forEach((item, index) => {
        const x = margin.left + index * xScale;
        const y = margin.top + chartHeight - (item.value / maxValue) * chartHeight;
        
        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      
      ctx.stroke();
      
      // Draw points
      data.forEach((item, index) => {
        const x = margin.left + index * xScale;
        const y = margin.top + chartHeight - (item.value / maxValue) * chartHeight;
        
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, 2 * Math.PI);
        ctx.fillStyle = '#3b82f6';
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        // Labels
        ctx.fillStyle = '#333';
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(item.name, x, margin.top + chartHeight + 20);
        ctx.fillText(item.value, x, y - 10);
      });
      
      // Chart title
      ctx.textAlign = 'center';
      ctx.font = 'bold 16px Arial';
      ctx.fillText('Ticket Trends Over Time', canvas.width / 2, margin.top - 10);
      
      resolve(canvas.toDataURL('image/png'));
    });
  };

  const getFiltersText = (filters) => {
    const activeFilters = [];
    if (filters.employeeId) {
      const employee = filteredData.employees.find(emp => emp._id === filters.employeeId);
      activeFilters.push(`Employee: ${employee?.teamMemberName || 'All'}`);
    }
    if (filters.signedStatus) {
      activeFilters.push(`Signed Status: ${filters.signedStatus}`);
    }
    if (filters.status) {
      activeFilters.push(`Ticket Status: ${filters.status}`);
    }
    if (filters.dateRange) {
      activeFilters.push(`Date Range: ${filters.dateRange}`);
    }
    
    return activeFilters.length > 0 
      ? `<div class="filters-info"><strong>Active Filters:</strong> ${activeFilters.join(', ')}</div>`
      : '';
  };

  const getChartData = (filteredData) => {
    const tickets = filteredData.tickets;
    
    // Status distribution
    const statusData = Object.entries(
      tickets.reduce((acc, ticket) => {
        const status = ticket.status || 'unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {})
    ).map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }));

    // Employee performance
    const employeeData = filteredData.employees.map(emp => {
      const empTickets = tickets.filter(t => t.assignedTo === emp._id);
      const completedTickets = empTickets.filter(t => 
        ['completed', 'resolved', 'closed'].includes(t.status)
      ).length;
      
      return {
        name: emp.teamMemberName,
        total: empTickets.length,
        completed: completedTickets,
        completionRate: empTickets.length > 0 ? Math.round((completedTickets / empTickets.length) * 100) : 0
      };
    }).filter(emp => emp.total > 0);

    // Trend data (last 6 months)
    const trendData = generateTrendData(tickets);

    return { statusData, employeeData, trendData };
  };

  const generateTrendData = (tickets) => {
    const months = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear();
      
      const monthTickets = tickets.filter(ticket => {
        const ticketDate = new Date(ticket.createdAt);
        return ticketDate.getMonth() === date.getMonth() && 
               ticketDate.getFullYear() === date.getFullYear();
      });
      
      months.push({
        name: `${monthName} ${year}`,
        value: monthTickets.length
      });
    }
    
    return months;
  };

  const getEmployeeStats = (filteredData) => {
    return filteredData.employees.map(emp => {
      const empTickets = filteredData.tickets.filter(t => t.assignedTo === emp._id);
      const empReviews = filteredData.reviews[emp._id] || [];
      
      const statusCounts = empTickets.reduce((acc, ticket) => {
        const status = ticket.status || 'unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      const signedTickets = empTickets.filter(t => t.isSigned).length;
      const completionRate = empTickets.length > 0 
        ? Math.round((empTickets.filter(t => 
            ['completed', 'resolved', 'closed'].includes(t.status)
          ).length / empTickets.length) * 100)
        : 0;

      const avgRating = empReviews.length > 0
        ? (empReviews.reduce((sum, r) => sum + r.rating, 0) / empReviews.length).toFixed(2)
        : 'N/A';

      return {
        ...emp,
        totalTickets: empTickets.length,
        signedTickets,
        completionRate,
        avgRating,
        statusCounts
      };
    });
  };

  const generateSummaryHTML = (filteredData) => {
    const stats = getSummaryStats(filteredData);
    
    return `
      <div class="summary-grid">
        <div class="summary-card" style="background: #dbeafe;">
          <div class="summary-number">${stats.totalEmployees}</div>
          <div>Employees</div>
        </div>
        <div class="summary-card" style="background: #dcfce7;">
          <div class="summary-number">${stats.totalTickets}</div>
          <div>Total Tickets</div>
        </div>
        <div class="summary-card" style="background: #f3e8ff;">
          <div class="summary-number">${stats.signedTickets}</div>
          <div>Signed Tickets</div>
        </div>
        <div class="summary-card" style="background: #ffedd5;">
          <div class="summary-number">${stats.avgRating}</div>
          <div>Avg Rating</div>
        </div>
      </div>
    `;
  };

  const getSummaryStats = (filteredData) => {
    const reviews = Object.values(filteredData.reviews).flat();
    const tickets = filteredData.tickets;

    return {
      totalEmployees: filteredData.employees.length,
      totalTickets: tickets.length,
      signedTickets: tickets.filter(t => t.isSigned).length,
      avgRating: reviews.length > 0 
        ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(2)
        : '0.00'
    };
  };

  const generateChartsHTML = (chartData, chartImages) => {
    return `
      <div class="chart-grid">
        <div class="chart-container">
          <div class="chart-title">Ticket Status Distribution</div>
          ${chartImages.statusPie ? `
            <img src="${chartImages.statusPie}" alt="Ticket Status Distribution" class="chart-image" />
            <div class="legend">
              ${chartData.statusData.map((item, index) => {
                const colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];
                return `
                  <div class="legend-item">
                    <div class="legend-color" style="background-color: ${colors[index % colors.length]}"></div>
                    <span>${item.name}: ${item.value}</span>
                  </div>
                `;
              }).join('')}
            </div>
          ` : '<p>No status data available</p>'}
        </div>

        <div class="chart-container">
          <div class="chart-title">Employee Performance</div>
          ${chartImages.employeeBar ? `
            <img src="${chartImages.employeeBar}" alt="Employee Performance" class="chart-image" />
          ` : '<p>No employee performance data available</p>'}
        </div>
      </div>

      ${chartImages.trendLine ? `
        <div class="chart-container">
          <div class="chart-title">Monthly Ticket Trends</div>
          <img src="${chartImages.trendLine}" alt="Monthly Trends" class="chart-image" />
        </div>
      ` : ''}

      <div class="chart-container">
        <div class="chart-title">Employee Performance Overview</div>
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr style="background: #f3f4f6;">
              <th style="padding: 10px; text-align: left; border: 1px solid #e5e7eb;">Employee</th>
              <th style="padding: 10px; text-align: center; border: 1px solid #e5e7eb;">Total Tickets</th>
              <th style="padding: 10px; text-align: center; border: 1px solid #e5e7eb;">Completed</th>
              <th style="padding: 10px; text-align: center; border: 1px solid #e5e7eb;">Completion Rate</th>
            </tr>
          </thead>
          <tbody>
            ${chartData.employeeData.map(emp => `
              <tr>
                <td style="padding: 10px; border: 1px solid #e5e7eb;">${emp.name}</td>
                <td style="padding: 10px; text-align: center; border: 1px solid #e5e7eb;">${emp.total}</td>
                <td style="padding: 10px; text-align: center; border: 1px solid #e5e7eb;">${emp.completed}</td>
                <td style="padding: 10px; text-align: center; border: 1px solid #e5e7eb;">${emp.completionRate}%</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  };


  // Updated generateEmployeeDetailsHTML to include per-employee charts
  const generateEmployeeDetailsHTML = (employeeStats) => {
    return `
      <div class="chart-container">
        <div class="chart-title">Detailed Employee Performance</div>
        ${employeeStats.map(emp => `
          <div class="employee-card">
            <div class="employee-header">
              <div>
                <strong style="font-size: 16px;">${emp.teamMemberName}</strong>
                <div style="color: #6b7280; font-size: 14px;">${emp.departmentName}</div>
              </div>
              <div style="text-align: right;">
                <div>Rating: <strong>${emp.avgRating}</strong></div>
                <div>Completion: <strong>${emp.completionRate}%</strong></div>
              </div>
            </div>
            
            <div class="stats-grid">
              <div>Total Tickets: <strong>${emp.totalTickets}</strong></div>
                         
            <div>Signed Tickets: <strong>${emp.signedTickets}</strong></div>
            </div>
            
            <div>
              <strong>Ticket Status Breakdown:</strong>
              <div class="status-grid">
                ${Object.entries(emp.statusCounts).map(([status, count]) => `
                  <div class="status-item">
                    ${status}: <strong>${count}</strong>
                  </div>
                `).join('')}
              </div>
            </div>

            <!-- NEW: Per-Employee Daily Completed Tickets Chart -->
            ${emp.trendChartImage ? `
              <div class="employee-chart-container">
                <div class="employee-chart-title">Daily Completed Tickets (Last 30 Days)</div>
                <img src="${emp.trendChartImage}" alt="Daily Completed Tickets for ${emp.teamMemberName}" class="chart-image" style="max-width: 100%; height: auto;" />
              </div>
            ` : `
              <div class="employee-chart-container">
                <div class="employee-chart-title">Daily Completed Tickets (Last 30 Days)</div>
                <div class="no-data">No completed tickets in the last 30 days</div>
              </div>
            `}
          </div>
        `).join('')}
      </div>
    `;
  };

  return (
    <div className="flex justify-end gap-4 mb-4">
      <button
        onClick={handlePrint}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 no-print"
      >
        🖨️ Print Report with Graphs
      </button>
      
    </div>
  );
  
};


// Enhanced Report Summary Component with Graphical Data
const ReportSummary = ({ filteredData, filters }) => {
  const stats = useMemo(() => {
    const reviews = Object.values(filteredData.reviews).flat();
    const tickets = filteredData.tickets;

    // Calculate status distribution for charts
    const statusDistribution = tickets.reduce((acc, ticket) => {
      const status = ticket.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const statusData = Object.entries(statusDistribution).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value
    }));

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

    return {
      totalEmployees: filteredData.employees.length,
      totalReviews: reviews.length,
      totalTickets: tickets.length,
      signedTickets: tickets.filter(t => t.isSigned).length,
      averageRating: reviews.length > 0 
        ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(2)
        : 0,
      completionRate: tickets.length > 0 
        ? ((tickets.filter(t => t.status === 'completed' || t.status === 'closed').length / tickets.length) * 100).toFixed(1)
        : 0,
      statusData,
      colors: COLORS
    };
  }, [filteredData]);

  return (
    <div className="space-y-6">
      {/* Main Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="text-2xl font-bold text-blue-600">{stats.totalEmployees}</div>
          <div className="text-sm text-blue-800">Employees</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="text-2xl font-bold text-green-600">{stats.totalTickets}</div>
          <div className="text-sm text-green-800">Total Tickets</div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <div className="text-2xl font-bold text-purple-600">{stats.signedTickets}</div>
          <div className="text-sm text-purple-800">Signed Tickets</div>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <div className="text-2xl font-bold text-orange-600">{stats.averageRating}</div>
          <div className="text-sm text-orange-800">Avg Rating</div>
        </div>
      </div>

      {/* Graphical Status Distribution */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Ticket Status Distribution</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats.statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {stats.statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={stats.colors[index % stats.colors.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-col justify-center">
            <div className="space-y-2">
              {stats.statusData.map((status, index) => (
                <div key={status.name} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: stats.colors[index % stats.colors.length] }}
                    ></div>
                    <span className="text-sm">{status.name}</span>
                  </div>
                  <span className="font-semibold">{status.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// Enhanced Performance Card Component
const EmployeePerformanceCard = ({ employee, reviews, tickets }) => {
  const [expanded, setExpanded] = useState(false);

  // Calculate statistics for this employee
  const stats = useMemo(() => {
    const employeeTickets = tickets.filter(t => t.assignedTo === employee._id);
    const signedTickets = employeeTickets.filter(t => t.isSigned);
    
    // Calculate status breakdown dynamically
    const statusCounts = employeeTickets.reduce((acc, t) => {
      const status = t.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // Prepare chart data for graphical display
    const chartData = Object.entries(statusCounts).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1), // Capitalize for display
      value
    }));

    return {
      totalTickets: employeeTickets.length,
      signedTickets: signedTickets.length,
      signRate: employeeTickets.length > 0 ? ((signedTickets.length / employeeTickets.length) * 100).toFixed(1) : 0,
      averageRating: reviews.length > 0 
        ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(2)
        : "N/A",
      statusBreakdown: statusCounts,
      chartData
    };
  }, [employee._id, reviews, tickets]);

  // Group reviews by period for detailed breakdown
  const groupedReviews = useMemo(() => {
    const byYear = {};
    reviews.forEach((r) => {
      const d = new Date(r.createdAt);
      const year = d.getFullYear();
      const month = d.toLocaleString("default", { month: "short" });
      const week = `Week ${Math.ceil(d.getDate() / 7)}`;
      
      if (!byYear[year]) byYear[year] = { months: {}, weeks: {} };
      if (!byYear[year].months[month]) byYear[year].months[month] = [];
      if (!byYear[year].weeks[week]) byYear[year].weeks[week] = [];
      
      byYear[year].months[month].push(r);
      byYear[year].weeks[week].push(r);
    });
    return byYear;
  }, [reviews]);

  return (
    <div className="mb-6 p-4 border rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
      {/* Header with expandable toggle */}
      <div 
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div>
          <h3 className="text-lg font-semibold text-gray-800">{employee.teamMemberName}</h3>
          <p className="text-sm text-gray-600">{employee.departmentName}</p>
        </div>
        <div className="text-right">
          <div className="flex gap-2 mb-1">
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
              Tickets: {stats.totalTickets}
            </span>
            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
              Signed: {stats.signedTickets}
            </span>
            <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">
              Sign Rate: {stats.signRate}%
            </span>
          </div>
          <div className="text-sm text-gray-500">
            Avg Rating: <span className="font-semibold">{stats.averageRating}</span>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="mt-4 space-y-4">
          {/* Signed Tickets Summary */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <h4 className="font-medium text-gray-700 mb-2">Signed Tickets Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Total Assigned:</span>
                <span className="ml-2 font-medium">{stats.totalTickets}</span>
              </div>
              <div>
                <span className="text-gray-600">Signed Tickets:</span>
                <span className="ml-2 font-medium text-green-600">{stats.signedTickets}</span>
              </div>
              <div>
                <span className="text-gray-600">Sign Completion Rate:</span>
                <span className="ml-2 font-medium">{stats.signRate}%</span>
              </div>
              <div>
                <span className="text-gray-600">Unsigned Tickets:</span>
                <span className="ml-2 font-medium text-orange-600">
                  {stats.totalTickets - stats.signedTickets}
                </span>
              </div>
            </div>
          </div>

          {/* Graphical Ticket Status Distribution */}
          <div className="bg-blue-50 p-3 rounded-lg">
            <h4 className="font-medium text-gray-700 mb-2">Ticket Status Distribution (Total Assigned: {stats.totalTickets})</h4>
            {stats.chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
                                    <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#3182ce" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-500 italic">No ticket status data available.</p>
            )}
          </div>

          {/* Detailed Reviews Breakdown */}
          {Object.keys(groupedReviews).length > 0 ? (
            Object.entries(groupedReviews).map(([year, data]) => (
              <div key={year} className="space-y-3">
                <h4 className="font-medium text-blue-700 border-b pb-1">{year} Performance</h4>
                
                {/* Monthly Breakdown */}
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Monthly Breakdown</h5>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border px-2 py-1 text-left">Month</th>
                          <th className="border px-2 py-1 text-left">Reviews</th>
                          <th className="border px-2 py-1 text-left">Avg Rating</th>
                          <th className="border px-2 py-1 text-left">Signed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(data.months).map(([month, monthReviews]) => {
                          const signedCount = monthReviews.filter(r => r.isSigned).length;
                          return (
                            <tr key={month} className="hover:bg-gray-50">
                              <td className="border px-2 py-1 font-medium">{month}</td>
                              <td className="border px-2 py-1">{monthReviews.length}</td>
                              <td className="border px-2 py-1">
                                {(monthReviews.reduce((a, b) => a + b.rating, 0) / monthReviews.length).toFixed(2)}
                              </td>
                              <td className="border px-2 py-1">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  signedCount > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {signedCount}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Recent Signed Tickets */}
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Recent Signed Tickets</h5>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {reviews
                      .filter(r => r.isSigned)
                      .slice(0, 5)
                      .map((review, idx) => (
                        <div key={idx} className="flex justify-between items-center text-xs bg-green-50 p-2 rounded">
                          <span className="truncate flex-1">{review.ticketSubject}</span>
                          <span className="ml-2 text-green-700">
                            {new Date(review.signedAt || review.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    {reviews.filter(r => r.isSigned).length === 0 && (
                      <p className="text-xs text-gray-500 italic">No signed tickets yet</p>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic text-sm">No performance data available for this period</p>
          )}
        </div>
      )}
    </div>
  );
};

// Export Report Component
const ExportReport = ({ filteredData }) => {
  const exportToCSV = () => {
    const headers = [
      "Ticket ID",
      "Subject",
      "Description",
      "Employee Name",
      "Department",
      "Status",
      "Signed Status",
      "Created At",
      "Updated At"
    ];

    const csvContent = [
      headers.join(","),
      ...filteredData.tickets.map(ticket => {
        const employee = filteredData.employees.find(e => e._id === ticket.assignedTo);
        return [
          `"${ticket._id}"`,
          `"${ticket.subject || ""}"`,
          `"${ticket.description || ""}"`,
          `"${employee?.teamMemberName || "Unassigned"}"`,
          `"${employee?.departmentName || ""}"`,
          `"${ticket.status}"`,
          ticket.isSigned ? "Signed" : "Not Signed",
          `"${new Date(ticket.createdAt).toLocaleString()}"`,
          `"${new Date(ticket.updatedAt).toLocaleString()}"`
        ].join(",");
      })
    ].join("\n");

    // Download CSV
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `filtered-tickets-report-${new Date()
      .toISOString()
      .split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="flex justify-end mb-4">
      <button
        onClick={exportToCSV}
        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
      >
        📊 Export Filtered Data
      </button>
    </div>
  );
};

// Loading component
const PerformanceLoading = () => (
  <div className="p-6 space-y-4">
    {[1, 2, 3].map(i => (
      <div key={i} className="p-4 border rounded-lg bg-white shadow animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-3"></div>
        <div className="flex gap-4 mb-4">
          <div className="h-6 bg-gray-200 rounded w-1/6"></div>
          <div className="h-6 bg-gray-200 rounded w-1/6"></div>
        </div>
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    ))}
  </div>
);

// Main component with enhanced filtering and reporting
function EmployeePerformance() {
  const { data, loading, error } = usePerformanceData();
  const [filters, setFilters] = useState({
    employeeId: '',
    signedStatus: '',
    status: '',
    dateRange: '',
    startDate: '',
    endDate: ''
  });

  // Apply filters to data
  const filteredData = useMemo(() => {
    let filteredEmployees = [...data.employees];
    let filteredReviews = { ...data.reviews };
    let filteredTickets = [...data.tickets];

    // Filter by employee
    if (filters.employeeId) {
      filteredEmployees = filteredEmployees.filter(emp => emp._id === filters.employeeId);
      filteredReviews = Object.fromEntries(
        Object.entries(filteredReviews).filter(([empId]) => empId === filters.employeeId)
      );
      filteredTickets = filteredTickets.filter(t => t.assignedTo === filters.employeeId);
    }

    // Filter by signed status
    if (filters.signedStatus) {
      if (filters.signedStatus === 'signed') {
        filteredTickets = filteredTickets.filter(t => t.isSigned);
        // Also filter reviews to only include signed ones
        filteredReviews = Object.fromEntries(
          Object.entries(filteredReviews).map(([empId, reviews]) => [
            empId,
            reviews.filter(r => r.isSigned)
          ]).filter(([_, reviews]) => reviews.length > 0)
        );
      } else if (filters.signedStatus === 'not-signed') {
        filteredTickets = filteredTickets.filter(t => !t.isSigned);
      }
    }

    // Filter by ticket status
    if (filters.status) {
      filteredTickets = filteredTickets.filter(t => t.status === filters.status);
    }

    // Filter by date range
    if (filters.dateRange) {
      const now = new Date();
      let startDate, endDate;

      switch (filters.dateRange) {
        case 'today':
          startDate = new Date(now.setHours(0, 0, 0, 0));
          endDate = new Date(now.setHours(23, 59, 59, 999));
          break;
        case 'week':
          const weekRange = getWeekRange(now);
          startDate = weekRange.start;
          endDate = weekRange.end;
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          break;
        case 'year':
          startDate = new Date(now.getFullYear(), 0, 1);
          endDate = new Date(now.getFullYear(), 11, 31);
          break;
        case 'custom':
          if (filters.startDate) startDate = new Date(filters.startDate);
          if (filters.endDate) endDate = new Date(filters.endDate);
          break;
      }

      if (startDate && endDate) {
        filteredTickets = filteredTickets.filter(t => {
          const ticketDate = new Date(t.createdAt);
          return ticketDate >= startDate && ticketDate <= endDate;
        });

        // Also filter reviews by date
        filteredReviews = Object.fromEntries(
          Object.entries(filteredReviews).map(([empId, reviews]) => [
            empId,
            reviews.filter(r => {
              const reviewDate = new Date(r.createdAt);
              return reviewDate >= startDate && reviewDate <= endDate;
            })
          ]).filter(([_, reviews]) => reviews.length > 0)
        );
      }
    }

    // Filter employees to only include those with data after filtering
    filteredEmployees = filteredEmployees.filter(emp => 
      filteredReviews[emp._id]?.length > 0 || 
      filteredTickets.some(t => t.assignedTo === emp._id)
    );

    return {
      employees: filteredEmployees,
      reviews: filteredReviews,
      tickets: filteredTickets
    };
  }, [data, filters]);

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'reset') {
      setFilters({
        employeeId: '',
        signedStatus: '',
        status: '',
        dateRange: '',
        startDate: '',
        endDate: ''
      });
    } else {
      setFilters(prev => ({
        ...prev,
        [filterType]: value
      }));
    }
  };

  // Get unique statuses for filter dropdown
  const uniqueStatuses = useMemo(() => {
    return [...new Set(data.tickets.map(t => t.status).filter(Boolean))];
  }, [data.tickets]);

  if (loading) return <PerformanceLoading />;
  
  if (error) return (
    <div className="p-6">
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
        <h3 className="font-bold mb-1">Error Loading Performance Data</h3>
        <p>{error}</p>
      </div>
    </div>
  );

  const employeesWithData = filteredData.employees.filter(emp => {
    const hasReviews = filteredData.reviews[emp._id]?.length > 0;
    const hasTickets = filteredData.tickets.some(t => t.assignedTo === emp._id);
    return hasReviews || hasTickets;
  });

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Employee Performance Dashboard</h2>
          <p className="text-gray-600">Comprehensive performance tracking with graphical analytics</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">
            {employeesWithData.length} Active Employees
          </span>
          <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm">
            {data.tickets.filter(t => t.isSigned).length} Signed Tickets
          </span>
        </div>
      </div>

      {/* Print Button */}
      <PrintButton 
        filteredData={filteredData} 
        filters={filters}
        employeesWithData={employeesWithData}
      />

      {/* Filters */}
      <PerformanceFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        employees={data.employees}
        uniqueStatuses={uniqueStatuses}
      />

      {/* Report Summary */}
      <ReportSummary filteredData={filteredData} filters={filters} />

      {/* Export Button */}
      <ExportReport filteredData={filteredData} />

      {/* Performance Cards */}
      {employeesWithData.length > 0 ? (
        <div className="space-y-6">
          {employeesWithData.map((emp) => (
            <EmployeePerformanceCard
              key={emp._id}
              employee={emp}
              reviews={filteredData.reviews[emp._id] || []}
              tickets={filteredData.tickets.filter(t => t.assignedTo === emp._id)}
            />
          ))}
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md">
          <h3 className="font-bold mb-2">No Performance Data Available</h3>
          <p>No employees match your current filters. Try adjusting the filter criteria.</p>
        </div>
      )}

      {/* Filter Active Indicator */}
      {Object.values(filters).some(val => val !== '' && val !== null) && (
        <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            📊 Showing filtered results. <button 
              onClick={() => handleFilterChange('reset', null)}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              Clear filters
            </button> to see all data.
          </p>
        </div>
      )}
    </div>
  );
}

export default EmployeePerformance;
